import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/system";
import { useSelector } from "react-redux";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { approveFinalDelivery } from "../../../../api/finalDelivery";
import { formatDate } from "../../../../utils/getEntitiesUtils";
import FinalDocumentsPreview from "../../../FinalDocuments/FinalDocumentsPreview";
import MySnackbar from "../../MySnackBar";

const FinalEvaluationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(2),
  padding: theme.spacing(3),
  borderRadius: "8px",
  backgroundColor: "#ffffff",
  width: "100%",
}));

/**
 * Un equipo visto por uno de sus evaluadores de la entrega final: los tres
 * documentos, quién aprobó y el botón para aprobar lo propio.
 *
 * La entrega queda aprobada cuando aprueban todos los evaluadores. Cada uno
 * aprueba lo suyo; el de un externo lo registra un admin. `group` es una fila
 * de GET /final-delivery/my-groups; `onChange` recibe el grupo actualizado.
 */
const FinalEvaluation = ({ group, onChange }) => {
  const user = useSelector((state) => state.user);
  const period = useSelector((state) => state.period);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    status: "",
  });

  const evaluators = group.evaluators || [];
  const mine = evaluators.find((e) => e.user_id === user?.id);
  const approvedCount = evaluators.filter((e) => e.approved_at).length;

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      const result = await approveFinalDelivery(user, group.id, mine.id, comment.trim());
      onChange({ ...group, approved: result.approved, evaluators: result.evaluators });
      setComment("");
      setNotification({
        open: true,
        message: result.approved
          ? "Aprobaste la entrega. Con tu aprobación, el equipo quedó listo para exponer."
          : "Aprobaste la entrega. Falta la aprobación de otros evaluadores.",
        status: "success",
      });
    } catch (error) {
      console.error("Error al aprobar la entrega final:", error);
      const detail = error?.response?.data?.detail;
      setNotification({
        open: true,
        message:
          [401, 404, 409].includes(error?.response?.status) && typeof detail === "string"
            ? detail
            : "No se pudo registrar la aprobación. Inténtalo de nuevo.",
        status: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FinalEvaluationContainer>
      <Typography variant="h4" align="center" gutterBottom>
        Entrega final · Equipo {group.group_number}
      </Typography>
      {group.final_report_title && (
        <Typography variant="h6" align="center" gutterBottom>
          {group.final_report_title}
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary" align="center">
        {[
          group.topic,
          group.director && `Director: ${group.director}`,
          group.students?.length && `Integrantes: ${group.students.join(", ")}`,
        ]
          .filter(Boolean)
          .join(" · ")}
      </Typography>
      {group.exhibition_date && (
        <Typography variant="body2" color="text.secondary" align="center">
          Exposición: {formatDate(group.exhibition_date)}
        </Typography>
      )}

      <Box sx={{ mt: 3 }}>
        <FinalDocumentsPreview
          groupId={group.id}
          groupNumber={group.group_number}
          periodId={period.id}
          user={user}
          group={group}
        />
      </Box>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6">Aprobación</Typography>
        <Alert severity={group.approved ? "success" : "info"} sx={{ my: 1 }}>
          {group.approved
            ? "Aprobada por todos los evaluadores: el equipo está listo para exponer."
            : `Aprobaron ${approvedCount} de ${evaluators.length}. La entrega queda aprobada cuando aprueban todos.`}
        </Alert>
        <List dense>
          {evaluators.map((evaluator) => (
            <ListItem key={evaluator.id} disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {evaluator.approved_at ? (
                  <CheckCircleIcon fontSize="small" color="success" />
                ) : (
                  <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={`${evaluator.name}${evaluator.id === mine?.id ? " (vos)" : ""}${
                  evaluator.is_external ? " (externo)" : ""
                }`}
                secondary={
                  evaluator.approved_at
                    ? `Aprobó el ${formatDate(evaluator.approved_at)}${
                        evaluator.approval_comment ? `: “${evaluator.approval_comment}”` : ""
                      }`
                    : "Pendiente"
                }
              />
            </ListItem>
          ))}
        </List>

        {mine && !mine.approved_at && (
          <Box sx={{ mt: 1 }}>
            <TextField
              multiline
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Comentario (opcional)"
              inputProps={{ maxLength: 2000 }}
              sx={{ mb: 2, width: "100%" }}
            />
            <Button
              variant="contained"
              color="success"
              onClick={handleApprove}
              disabled={submitting || !group.final_report_date}
              fullWidth
            >
              Aprobar entrega final
            </Button>
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              {group.final_report_date
                ? "La aprobación no se puede deshacer desde acá. Si necesitás que el equipo corrija algo, escribile antes de aprobar."
                : "El equipo todavía no subió los tres documentos: se puede aprobar cuando la entrega esté completa."}
            </Typography>
          </Box>
        )}
      </Box>

      <MySnackbar
        open={notification.open}
        handleClose={() => setNotification({ ...notification, open: false })}
        message={notification.message}
        status={notification.status}
      />
    </FinalEvaluationContainer>
  );
};

export default FinalEvaluation;
